// BM-H5047 (Vanessa's spec, 4 Nov 2026): "Submit for review" on the matter
// page and the supervision board. Opens a card in Ready for review (or
// Drafting) with a link to the live file in SharePoint. The board holds
// links, never files, so there is deliberately no upload control here.
import { useState } from "react";
import axios from "axios";
import { Send, Loader2 } from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";

export const ITEM_TYPES = [
  { key: "letter", label: "Letter" },
  { key: "email", label: "Email" },
  { key: "advice", label: "Advice" },
  { key: "memo", label: "Memo" },
  { key: "court", label: "Court document" },
  { key: "other", label: "Other" },
];

function newRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function looksLikeSharePoint(url) {
  try {
    const u = new URL((url || "").trim());
    return u.protocol === "https:" && /(^|\.)sharepoint\.com$/i.test(u.hostname);
  } catch {
    return false;
  }
}

const fieldCls = "w-full rounded-md border border-white/10 bg-bonsai-bg px-3 py-2 text-sm text-white";

export default function SubmitForReviewDialog({ matterRef = "", onSubmitted, trigger }) {
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState(matterRef);
  const [title, setTitle] = useState("");
  const [itemType, setItemType] = useState("letter");
  const [link, setLink] = useState("");
  const [note, setNote] = useState("");
  const [clientFacing, setClientFacing] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [startIn, setStartIn] = useState("ready_for_review");
  const [requestId, setRequestId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const start = () => {
    setRef(matterRef); setTitle(""); setItemType("letter"); setLink(""); setNote("");
    setClientFacing(true); setDueDate(""); setStartIn("ready_for_review");
    setRequestId(newRequestId()); setError(""); setOpen(true);
  };

  const linkOk = looksLikeSharePoint(link);
  const ready = ref.trim() && title.trim() && linkOk && !busy;

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/supervision/cards`, {
        matter_reference: ref.trim(), title: title.trim(), item_type: itemType,
        sharepoint_url: link.trim(), note, client_facing: clientFacing || itemType === "court",
        court: itemType === "court", due_date: dueDate || null, start_in: startIn,
        request_id: requestId,
      });
      setOpen(false);
      onSubmitted && onSubmitted(data.card);
    } catch (err) {
      setError(errorMessage(err, "The card could not be created."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {trigger ? trigger(start) : (
        <Button variant="outline" size="sm" onClick={start} data-testid="submit-for-review-open">
          <Send className="w-4 h-4 mr-1" /> Submit for review
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="submit-for-review-dialog">
          <DialogHeader>
            <DialogTitle>Submit for review</DialogTitle>
            <DialogDescription>
              Paste the link to the document in the matter's SharePoint folder. The board keeps the link, never a copy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {!matterRef && (
              <label className="block">
                <span className="text-xs text-slate-400">Matter reference</span>
                <Input value={ref} onChange={e => setRef(e.target.value.toUpperCase())}
                  placeholder="BL.VEC.002.1234" data-testid="sfr-matter" />
              </label>
            )}
            <label className="block">
              <span className="text-xs text-slate-400">What is it?</span>
              <Input value={title} onChange={e => setTitle(e.target.value)} maxLength={200}
                placeholder="Letter to Slater re WP counter" data-testid="sfr-title" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-400">Type</span>
                <select className={fieldCls} value={itemType} onChange={e => setItemType(e.target.value)}
                  data-testid="sfr-type">
                  {ITEM_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-slate-400">Due (optional)</span>
                <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="sfr-due" />
              </label>
            </div>
            <label className="block">
              <span className="text-xs text-slate-400">SharePoint link</span>
              <Input value={link} onChange={e => setLink(e.target.value)}
                placeholder="https://...sharepoint.com/..." data-testid="sfr-link" />
              {link && !linkOk && (
                <span className="text-xs text-status-red" data-testid="sfr-link-error">
                  This must be the document's SharePoint link (https://...sharepoint.com/...).
                </span>
              )}
            </label>
            <label className="block">
              <span className="text-xs text-slate-400">Covering note (optional)</span>
              <textarea className={fieldCls} rows={3} value={note} onChange={e => setNote(e.target.value)}
                maxLength={4000} data-testid="sfr-note" />
            </label>
            <div className="flex flex-wrap gap-4 text-xs text-slate-300">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={clientFacing || itemType === "court"} disabled={itemType === "court"}
                  onChange={e => setClientFacing(e.target.checked)} data-testid="sfr-client-facing" />
                Goes to the client or the other side
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={startIn === "drafting"}
                  onChange={e => setStartIn(e.target.checked ? "drafting" : "ready_for_review")}
                  data-testid="sfr-drafting" />
                Still drafting (show it as coming)
              </label>
            </div>
            <p className="text-[11px] text-slate-500">Write it as if the client will read it.</p>
            {error && <p className="text-status-red text-xs" data-testid="sfr-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!ready} data-testid="sfr-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {startIn === "drafting" ? "Add to Drafting" : "Submit for review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
